import { createApiClient, getApiErrorInfo } from './api';
import { config } from '../config';
import type { AxiosInstance } from 'axios';
import { CourseType, type CourseType as CourseTypeValue } from './courseService';
import { hasWebinarEnded, isWebinarStartWithinBuffer } from './webinarTiming';
import { track } from './analytics';

function getCertificateDownloadFileName(contentDisposition: string | undefined, fallbackTitle: string | undefined): string {
  const utf8Match = contentDisposition?.match(/filename\*=UTF-8''([^;]+)/i);
  const asciiMatch = contentDisposition?.match(/filename="?([^";]+)"?/i);
  const headerFileName = utf8Match?.[1] ?? asciiMatch?.[1];

  if (headerFileName) {
    return decodeURIComponent(headerFileName);
  }

  const safeTitle = (fallbackTitle ?? 'webinar-certificate')
    .replace(/[^a-z0-9]+/gi, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();

  return `${safeTitle || 'webinar-certificate'}.pdf`;
}

export interface Registration {
  id: string;
  userId: string;
  courseId: number;
  courseInstanceId?: number;
  courseTitle: string;
  courseType: CourseTypeValue;
  sourceSystemCourseId?: string;
  status: string; // pending, enrolled, in_progress, completed, cancelled, failed
  // TODO: Review UI handling for 'pending' and 'failed' statuses — currently no distinct UX for these states
  registeredAt: string;
  completedAt?: string;
  startDate?: string;
  endDate?: string;
  courseUrl?: string;
  joinUrl?: string;
  certificateUrl?: string;
}

export interface RegistrationStatus {
  isRegistered: boolean;
  status?: string;
  registeredAt?: string;
}

export interface RegistrationTimingInput {
  status: string;
  courseType: CourseTypeValue;
  startDate?: string;
  endDate?: string;
}

export function isRegistrationActive(registration: RegistrationTimingInput): boolean {
  if (
    registration.status === 'completed' ||
    registration.status === 'cancelled' ||
    registration.status === 'transferred' ||
    registration.status === 'failed'
  ) {
    return false;
  }
  if (registration.courseType !== CourseType.Webinar) return true;
  if (registration.status === 'in_progress') return true;
  if (hasWebinarEnded(registration.startDate, registration.endDate)) return false;
  if (registration.status === 'pending' || registration.status === 'enrolled') return true;
  return isWebinarStartWithinBuffer(registration.startDate);
}

export function isRegistrationWebinarEnded(registration: RegistrationTimingInput): boolean {
  if (registration.courseType !== CourseType.Webinar) return false;
  return hasWebinarEnded(registration.startDate, registration.endDate);
}

class RegistrationService {
  private api: AxiosInstance;

  constructor() {
    this.api = createApiClient(config.api.courses);
  }

  /**
   * Register the current user for a course.
   * Returns the created registration, or throws on access/conflict errors.
   */
  async registerForCourse(courseId: number): Promise<Registration> {
    const response = await this.api.post<Registration>(`/api/registrations/${courseId}`);
    track('course_registered', { course_id: String(courseId), course_type: CourseType.OnDemand });
    return response.data;
  }

  /**
   * Cancel the current user's registration for a course.
   */
  async cancelRegistration(courseId: number): Promise<void> {
    await this.api.delete(`/api/registrations/${courseId}`);
  }

  /**
   * Get registration history for the current user.
   */
  async getUserRegistrations(): Promise<Registration[]> {
    try {
      const response = await this.api.get<Registration[]>('/api/registrations');
      return response.data;
    } catch (error: unknown) {
      console.error('Failed to fetch registrations:', error);
      const apiError = getApiErrorInfo(error);
      console.error('Registration error details:', apiError);
      return [];
    }
  }

  /**
   * Check whether the current user is registered for a specific course.
   */
  async getRegistrationStatus(courseId: number): Promise<RegistrationStatus> {
    try {
      const response = await this.api.get<RegistrationStatus>(`/api/registrations/${courseId}/status`);
      return response.data;
    } catch (error: unknown) {
      console.error('Failed to fetch registration status:', error);
      const apiError = getApiErrorInfo(error);
      console.error('Registration status error details:', apiError);
      return { isRegistered: false };
    }
  }

  async downloadWebinarCertificate(registrationId: string, courseTitle?: string): Promise<void> {
    const response = await this.api.get<Blob>(`/api/registrations/${registrationId}/certificate`, {
      responseType: 'blob',
    });

    const contentDispositionHeader = typeof response.headers['content-disposition'] === 'string'
      ? response.headers['content-disposition']
      : undefined;
    const contentTypeHeader = typeof response.headers['content-type'] === 'string'
      ? response.headers['content-type']
      : undefined;

    const blob = response.data instanceof Blob
      ? response.data
      : new Blob([response.data], { type: contentTypeHeader || 'application/octet-stream' });

    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');

    anchor.href = objectUrl;
    anchor.download = getCertificateDownloadFileName(contentDispositionHeader, courseTitle);

    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
    track('certificate_downloaded', {});
  }
}

export default new RegistrationService();
